<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { getColorFromScore } from '@/utils'
import EditGroupSidePanel from '@/components/SidePanels/EditGroup.vue'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import ExamVersionDropdown from '@/components/ExamVersionDropdown.vue'
import type { Teach } from '@pocketprep/types'

const {
    Button: PocketButton,
    SkeletonLoader,
} = UIKit

const showEditGroupSidePanel = ref(false)
const editingGroupId = ref<string | null>(null)
const isLoading = ref(false)

const currentOrgInstructor = computed(() => instructorModule.getters.getCurrentOrgInstructor())

const currentGroup = computed((): Teach.Class.OrgGroupJSON | null => {
    return groupModule.getters.getCurrentGroup()
})

const visibleGroups = computed(() => groupModule.getters.getCurrentVisibleOrgGroups())

const groupAggStats = computed(() => statsModule.getters.getCurrentGroupAggStats())

const currentGroupsStudentCount = computed(() => {
    return currentGroup.value && currentGroup.value.orgStudents.length
})

const canEditGroup = computed(() => {
    return currentGroup.value
        && currentGroup.value.type !== 'system'
        && (
            currentGroup.value.owner?.objectId === currentOrgInstructor.value?.objectId
            || currentGroup.value.isPubliclyEditable === true
        )
})

const formattedGroupName = computed(() => {
    if (!currentGroup.value) {
        return {
            firstLine: '',
            secondLine: '',
        }
    }

    const groupNameWords = currentGroup.value.name.split(' ')
    return groupNameWords.reduce<{
        firstLine: string
        secondLine: string
    }>((acc) => {
        const newFirstLine = `${acc.firstLine}${acc.firstLine ? ' ' : ''}${acc.secondLine.split(' ')[0]}`
        const newSecondLine = acc.secondLine.split(' ').slice(1).join(' ')
        const originalLengthDiff = Math.abs(acc.secondLine.length - acc.firstLine.length)
        const newLengthDiff = Math.abs(newSecondLine.length - newFirstLine.length)
        if (newLengthDiff < originalLengthDiff) {
            acc.firstLine = newFirstLine
            acc.secondLine = newSecondLine
        }

        return acc
    }, {
        firstLine: '',
        secondLine: groupNameWords.join(' '),
    })
})

const currentExamMetadata = computed(() => examModule.getters.getCurrentExamMetadata())

const overallScoreClasses = computed(() => {
    const className = `group-stats-head__stat-value--${getColorFromScore(groupAggStats.value.overallScorePct)}`
    return {
        [className]: groupAggStats.value.answeredCount > 0,
    }
})

const clickEditGroup = () => {
    if (currentGroup.value) {
        editingGroupId.value = currentGroup.value.objectId
        showEditGroupSidePanel.value = true
    }
}

const route = useRoute()
const router = useRouter()

const closeEditGroupSidePanel = () => {
    showEditGroupSidePanel.value = false
    editingGroupId.value = null

    // If the current group was deleted, go to the first visible group
    if (visibleGroups.value.length) {
        const currentGroupFound = visibleGroups.value.find(group => group.objectId === route.params.groupId)
        if (!currentGroupFound) {
            router.push({
                name: 'group-stats',
                params: {
                    groupId: visibleGroups.value[0]?.objectId,
                },
                query: { ...route.query },
            })
        }
    }
}

onMounted(async () => {
    isLoading.value = true
    const routeGroupId = route.params.groupId as string

    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructor(),
        groupModule.actions.fetchCurrentVisibleOrgGroups(),
        groupModule.actions.fetchCurrentGroup(),
        examModule.actions.fetchCurrentGroupExam(),
        statsModule.actions.fetchCurrentGroupAggStats(),
        examModule.actions.fetchExamMetadata(),
        statsModule.actions.fetchCurrentGroupAggStats(),
        groupModule.actions.updateCurrentGroupId(routeGroupId),
    ])

    isLoading.value = false
})
</script>

<template>
    <div>
        <EditGroupSidePanel
            v-if="showEditGroupSidePanel"
            :group-id="editingGroupId || ''"
            @close="closeEditGroupSidePanel"
        />
        <div class="group-stats-head">
            <div class="group-stats-head__section-head">
                <ExamVersionDropdown class="group-stats-head__exam" />
                <div class="group-stats-head__head-section-right">
                    <PocketButton
                        v-show="canEditGroup && !isLoading"
                        class="group-stats-head__edit-group-btn"
                        type="tertiary"
                        @click="clickEditGroup"
                    >
                        Edit Group
                    </PocketButton>
                </div>
            </div>
            <div class="group-stats-head__section-body">
                <div class="group-stats-head__group-name">
                    <div> {{ formattedGroupName.firstLine }}</div>
                    <div> {{ formattedGroupName.secondLine }} </div>
                </div>
                <div class="group-stats-head__stats-summary">
                    <div class="group-stats-head__stat">
                        <div class="group-stats-head__stat-value">
                            {{ currentGroupsStudentCount }}
                        </div>
                        <div class="group-stats-head__stat-label">
                            Students
                        </div>
                    </div>
                    <div class="group-stats-head__stat">
                        <SkeletonLoader
                            v-if="isLoading || (groupAggStats.overallScorePct === 0 && groupAggStats.isLoading)"
                            class="group-stats-head__stat-loader"
                        />
                        <div
                            v-else-if="!isLoading"
                            class="group-stats-head__stat-value"
                            :class="overallScoreClasses"
                        >
                            {{ `${groupAggStats.overallScorePct}%` }}
                        </div>
                        <div class="group-stats-head__stat-label">
                            Overall Score
                        </div>
                    </div>
                    <div class="group-stats-head__stat">
                        <div class="group-stats-head__stat-value">
                            <span
                                v-if="isLoading"
                            >
                                <SkeletonLoader
                                    class="group-stats-head__stat-loader"
                                />
                            </span>
                            <span
                                v-else
                            >
                                {{ groupAggStats.answeredCount }}
                            </span>
                            <span class="group-stats-head__stat-value-divider">/</span>
                            <span>{{
                                currentExamMetadata
                                    ? currentExamMetadata.itemCount - currentExamMetadata.archivedCount
                                    : groupAggStats.totalCount
                            }}</span>
                        </div>
                        <div class="group-stats-head__stat-label">
                            Avg Answered / Total Questions
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.group-stats-head {
    margin-bottom: 50px;

    &__section-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        position: relative;
    }

    &__section-body {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
    }

    &__exam {
        margin-bottom: 24px;
    }

    &__edit-group-btn {
        padding: 0 !important;
    }

    &__group-name {
        max-width: 320px;
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        overflow-wrap: break-word;
    }

    &__stats-summary {
        display: flex;
        margin-bottom: 7px;
    }

    &__stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 15px;

        &:last-child {
            margin-right: 0;
        }
    }

    &__stat-loader {
        height: 24px;
        width: 60px;
        margin: 3px 0 6px 0;
        float: left;
    }

    &__stat-value {
        font-size: 24px;
        font-weight: 600;
        letter-spacing: 0.24px;
        line-height: 32.78px;
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: -5px;
            width: 94px;
            height: 44px;
            margin-left: -52px;
        }

        &--green::before {
            background: url('@/assets/images/highlight-green.svg') no-repeat center center;
        }

        &--red::before {
            background: url('@/assets/images/highlight-red.svg') no-repeat center center;
        }

        &--yellow::before {
            background: url('@/assets/images/highlight-yellow.svg') no-repeat center center;
        }
    }

    &__stat-value-divider {
        margin: 0 10px;
    }

    &__stat-label {
        font-size: 12px;
        color: $slate-01;
        white-space: nowrap;
        margin-top: 2px;
    }
}
</style>
